import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { boardMemberApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getBoardJoinLimitContent, isBoardJoinLimitErrorCode } from '../utils/boardJoinLimitMessages';

interface JoinBoardError {
  title: string;
  message: string;
  secondary?: string;
  cta?: { label: string; redirect: string };
}

export const JoinBoard: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<JoinBoardError | null>(null);
  const [success, setSuccess] = useState(false);
  const [boardTitle, setBoardTitle] = useState<string>('');
  const joinStartedRef = useRef(false);

  useEffect(() => {
    if (user && token && !joinStartedRef.current) {
      joinStartedRef.current = true;
      handleJoinBoard();
    }
  }, [user, token]);

  const handleJoinBoard = async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await boardMemberApi.acceptInviteLink(token);
      setBoardTitle(response.data.board.title);
      setSuccess(true);
      
      // Rediriger vers le tableau après 2 secondes
      setTimeout(() => {
        navigate(`/boards/${response.data.board.id}`);
      }, 2000);
    } catch (err: any) {
      const data = err.response?.data;
      const code = data?.error;

      if (err.response?.status === 404) {
        setError({
          title: 'Lien invalide',
          message: 'Ce lien d\'invitation est invalide ou a expiré.',
        });
      } else if (isBoardJoinLimitErrorCode(code)) {
        const content = getBoardJoinLimitContent(code);
        setError({
          title: content.title,
          message: content.message,
          secondary: 'Vous pourrez retenter de rejoindre ce tableau via ce même lien une fois cette étape effectuée.',
          cta: data?.redirect ? { label: content.ctaLabel, redirect: data.redirect } : undefined,
        });
      } else {
        // Le backend renvoie le cas "déjà membre" dans `error` plutôt que `message`.
        const alreadyMemberText = [data?.message, data?.error].find(
          (text): text is string => typeof text === 'string' && text.includes('déjà membre')
        );

        if (alreadyMemberText) {
          setError({ title: 'Déjà membre', message: alreadyMemberText });
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          setError({
            title: 'Erreur',
            message: typeof data?.message === 'string' ? data.message : 'Erreur lors de l\'acceptation de l\'invitation.',
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="join-board-page">
        <div className="join-board-container">
          <div className="join-board-card">
            <h1>Invitation à rejoindre un tableau</h1>
            <p>Vous devez être connecté pour accepter cette invitation.</p>
            <div className="join-board-actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (token) {
                    localStorage.setItem('post_auth_redirect', `/join/${token}`);
                  }

                  navigate('/login', {
                    state: { returnTo: `/join/${token}` }
                  });
                }}
              >
                Se connecter
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (token) {
                    localStorage.setItem('post_auth_redirect', `/join/${token}`);
                  }

                  navigate('/register', {
                    state: { returnTo: `/join/${token}` }
                  });
                }}
              >
                Créer un compte
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="join-board-page">
      <div className="join-board-container">
        <div className="join-board-card">
          {isLoading ? (
            <>
              <div className="join-board-spinner"></div>
              <h2>Acceptation de l'invitation...</h2>
              <p>Veuillez patienter</p>
            </>
          ) : success ? (
            <>
              <div className="join-board-success">✓</div>
              <h2>Bienvenue !</h2>
              <p>Vous avez rejoint le tableau <strong>{boardTitle}</strong> avec succès.</p>
              <p className="join-board-redirect">Redirection en cours...</p>
            </>
          ) : error ? (
            <>
              <div className="join-board-error">✕</div>
              <h2>{error.title}</h2>
              <p>{error.message}</p>
              {error.secondary && <p className="join-board-redirect">{error.secondary}</p>}
              <div className="join-board-actions">
                {error.cta && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(error.cta!.redirect)}
                  >
                    {error.cta.label}
                  </button>
                )}
                <button
                  className={error.cta ? 'btn btn-secondary' : 'btn btn-primary'}
                  onClick={() => navigate('/dashboard')}
                >
                  Retour aux tableaux
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
